import {
  TractorAxlesFormName,
  TractorFieldNames,
} from '@contracts/order/fields/TractorInformationFields';
import { FloridaVehicleConfigurationValues } from '@contracts/order/valueNormalizers/FloridaVehicleConfiguration';
import { OntarioVehicleTypeValues } from '@contracts/order/valueNormalizers/OntarioVehicleType';
import { State } from '@contracts/order/valueNormalizers/State';
import { TractorBodyStyle } from '@contracts/order/valueNormalizers/TractorBodyStyle';
import { TractorFuelType } from '@contracts/order/valueNormalizers/TractorFuelType';
import { TractorMake } from '@contracts/order/valueNormalizers/TractorMake';
import { TractorType } from '@contracts/order/valueNormalizers/TractorType';
import { AxleFormName } from '@src/permitField/AxleInformationFields';
import {
  BooleanField,
  DropdownField,
  OptionalStringField,
  PositiveIntegerField,
  StringField,
} from '@src/permitField/PermitFields';
import { TractorAxlesFormUtils } from '@src/permitField/TractorAxlesFormUtils';
import type { StringSchema } from 'yup';
import * as yup from 'yup';

export class TractorIdField extends StringField {
  public static fieldName: string = TractorFieldNames.TractorId;
}

export class TractorUnitNumberField extends StringField {
  public static fieldName: string = TractorFieldNames.TractorUnitNumber;
}

export class TractorMakeField extends StringField {
  public static fieldName: string = TractorFieldNames.TractorMake;
  public static options = TractorMake;
  public static validator = yup.string().trim().required();
}

export class TractorYearField extends PositiveIntegerField {
  public static fieldName: string = TractorFieldNames.TractorYear;
}

export class TractorVINField extends StringField {
  public static fieldName: string = TractorFieldNames.TractorVIN;
  public static validator: StringSchema = yup
    .string()
    .trim()
    .transform((value: string) => value.toUpperCase())
    .when(TractorYearField.fieldName, {
      is: (year: number) => year > 1980,
      then: (schema) =>
        schema
          .length(17, 'isVin')
          .matches(/^[A-Z0-9]+$/, { message: 'isVin' })
          .required(),
      otherwise: (schema) => schema.trim().required(),
    });
}

export class TractorModelNumberField extends OptionalStringField {
  public static fieldName: string = TractorFieldNames.TractorModelNumber;
}

export class TractorTypeField extends DropdownField {
  public static fieldName: string = TractorFieldNames.TractorType;
  public static options = TractorType;
  public static validator = yup
    .string()
    .oneOf(TractorTypeField.options)
    .nullable();
}

export class TractorFuelTypeField extends DropdownField {
  public static fieldName: string = TractorFieldNames.TractorFuelType;
  public static options = TractorFuelType;
  public static validator = yup
    .string()
    .oneOf(TractorFuelTypeField.options)
    .nullable();
}

export class TractorBodyStyleField extends DropdownField {
  public static fieldName: string = TractorFieldNames.TractorBodyStyle;
  public static options = TractorBodyStyle;
  public static validator = yup
    .string()
    .oneOf(TractorBodyStyleField.options)
    .nullable();
}

export class TractorAxleCountField extends DropdownField {
  public static fieldName: string = TractorFieldNames.TractorAxleCount;
  public static options = Array.from({ length: 21 }, (_, i) =>
    (i + 1).toString(),
  );
  public static validator = yup
    .string()
    .oneOf(TractorAxleCountField.options)
    .transform((value: string) =>
      value === '' || value === '0' ? undefined : value,
    )
    .when([TractorAxlesFormName, AxleFormName], {
      is: (axles: unknown[]) => !axles || axles.length === 0,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.notRequired(),
    });
}

export class VehicleOfSpecialDesignField extends BooleanField {
  public static fieldName: string = TractorFieldNames.VehicleOfSpecialDesign;
}

export class TractorLicensePlateNumberField extends StringField {
  public static fieldName: string = TractorFieldNames.TractorLicensePlateNumber;
}

export class TractorFloridaVehicleConfigurationField extends DropdownField {
  public static fieldName: string =
    TractorFieldNames.TractorFloridaVehicleConfiguration;
  public static options = FloridaVehicleConfigurationValues;
  public static validator = yup
    .string()
    .oneOf(TractorFloridaVehicleConfigurationField.options)
    .required();
}

export class TractorOntarioVehicleTypeField extends DropdownField {
  public static fieldName: string = TractorFieldNames.TractorOntarioVehicleType;
  public static options = OntarioVehicleTypeValues;
  public static validator = yup
    .string()
    .oneOf(TractorOntarioVehicleTypeField.options)
    .required();
}

export class TractorLicensePlateStateField extends DropdownField {
  public static fieldName: string = TractorFieldNames.TractorLicensePlateState;
  public static options = Object.values(State);
  public static validator = yup
    .string()
    .oneOf(TractorLicensePlateStateField.options)
    .required();
}

export const tractorInformationFormField = {
  [TractorUnitNumberField.fieldName]: TractorUnitNumberField.validator,
  [TractorMakeField.fieldName]: TractorMakeField.validator,
  [TractorVINField.fieldName]: TractorVINField.validator,
  [TractorYearField.fieldName]: TractorYearField.validator,
  [TractorModelNumberField.fieldName]: TractorModelNumberField.validator,
  [TractorTypeField.fieldName]: TractorTypeField.validator,
  [TractorFuelTypeField.fieldName]: TractorFuelTypeField.validator,
  [TractorBodyStyleField.fieldName]: TractorBodyStyleField.validator,
  [VehicleOfSpecialDesignField.fieldName]:
    VehicleOfSpecialDesignField.validator,
  [TractorLicensePlateNumberField.fieldName]:
    TractorLicensePlateNumberField.validator,
  [TractorLicensePlateStateField.fieldName]:
    TractorLicensePlateStateField.validator,
  [TractorFloridaVehicleConfigurationField.fieldName]:
    TractorFloridaVehicleConfigurationField.validator,
  [TractorOntarioVehicleTypeField.fieldName]:
    TractorOntarioVehicleTypeField.validator,
};

export const tractorInformationFields = {
  ...tractorInformationFormField,
  [TractorAxleCountField.fieldName]: TractorAxleCountField.validator,
};

export const hasNoAxlesFields = (fields: string[]) => {
  return (
    !fields.includes(AxleFormName) &&
    !fields.includes(TractorAxlesFormUtils.fieldName)
  );
};
export const shouldDisplayTractorAxlesCount = (fields: string[]) =>
  fields.includes(TractorAxleCountField.fieldName) && hasNoAxlesFields(fields);
